import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Button } from "../ui/Button/Button";

function DropdownButton({ buttonLabel, options, variant = "default", dropdownWidth = "full", selectedValue = null }) {
  const [showMenu, setShowMenu] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(buttonLabel);
  const ref = useRef();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedLabel(buttonLabel);
  }, [buttonLabel]);

  const handleClick = (option) => {
    console.log("Selected option:", option.label); // Debugging line

    // Check if this option is currently selected (by value if available, otherwise by label)
    const isCurrentlySelected = selectedValue
      ? option.value === selectedValue
      : selectedLabel === option.label;

    if (isCurrentlySelected) {
      setSelectedLabel(buttonLabel);
      if (option.resetAction) option.resetAction();
    } else {
      setSelectedLabel(option.label);
      if (option.action) option.action();
    }
    setShowMenu(false);
  };

  // Button styles based on variant
  const getButtonStyles = () => {
    if (variant === "link") {
      return "inline-flex items-center gap-1 text-red-500 font-medium hover:text-red-600 text-sm md:text-base px-0 py-0 border-0 shadow-none bg-transparent";
    }
    return "flex justify-between items-center w-full px-4 py-2 mt-1 text-red-500 bg-white border-2 border-red-400 rounded-full shadow-md font-semibold transition-all duration-200 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200";
  };

  // Dropdown width styles
  const getDropdownWidthStyles = () => {
    if (dropdownWidth === "auto") {
      return "w-48 max-h-80 overflow-y-auto";
    }
    return "w-full";
  };

  // Dropdown item padding
  const getDropdownItemPadding = () => {
    if (dropdownWidth === "auto") {
      return "px-4 py-2";
    }
    return "px-6 py-2";
  };

  return (
    <div className={`${variant === "link" ? "" : "mt-5"} relative inline-block ${dropdownWidth === "full" ? "w-full max-w-xs" : ""}`} ref={ref}>
      <Button
        variant={variant === "link" ? "link" : "custom"}
        size="md"
        onClick={() => setShowMenu((prev) => !prev)}
        className={getButtonStyles()}
      >
        <span className={variant === "link" ? "" : "truncate"}>{selectedLabel}</span>
        <svg
          className={`w-${variant === "link" ? "4" : "5"} h-${variant === "link" ? "4" : "5"} transform transition-transform duration-200 ${showMenu ? "rotate-180" : ""
            }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </Button>
      {showMenu && (
        <div
          className={`absolute ${variant === "link" ? "top-full left-0 mt-1" : "left-0 mt-2"} ${getDropdownWidthStyles()} bg-white border-2 border-red-200 rounded-2xl shadow-2xl z-50 animate-dropdown overflow-hidden`}
          style={{ minWidth: "100%" }}
        >
          {options.map((option, index) => {
            // Determine if this option is selected (by value if available, otherwise by label)
            const isSelected = selectedValue
              ? option.value === selectedValue
              : selectedLabel === option.label;

            return (
              <div
                key={option.id}
                className={`${getDropdownItemPadding()} cursor-pointer flex items-center transition-all duration-150 ${index === 0 ? "rounded-t-2xl" : ""
                  } ${index === options.length - 1 ? "rounded-b-2xl" : ""
                  } ${isSelected
                    ? "bg-red-50 text-red-600 font-bold"
                    : "hover:font-semibold hover:text-red-500 text-neutral-green-100-500"
                  }`}
                onClick={() => handleClick(option)}
              >
                {option.label}
                {isSelected && (
                  <svg
                    className="w-4 h-4 ml-auto text-red-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Animation for dropdown */}
      <style>
        {`
          .animate-dropdown {
            animation: dropdownIn 0.18s cubic-bezier(.4,0,.2,1);
          }
          @keyframes dropdownIn {
            0% { opacity: 0; transform: translateY(-10px) scale(0.98);}
            100% { opacity: 1; transform: translateY(0) scale(1);}
          }
        `}
      </style>
    </div>
  );
}

DropdownButton.propTypes = {
  buttonLabel: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      label: PropTypes.string.isRequired,
      action: PropTypes.func,
      resetAction: PropTypes.func,
      value: PropTypes.any,
    })
  ).isRequired,
  variant: PropTypes.oneOf(["default", "link"]),
  dropdownWidth: PropTypes.oneOf(["full", "auto"]),
  selectedValue: PropTypes.any,
};

export default DropdownButton;
